import React from 'react';

import {MarkdownInline} from '../../../components/utils/md';
import {imageUrl} from '../../../components/utils';
import {useGallery} from '../../../components/gallery';
import {Link} from '../../../lib/routes';
import {imageSrcSet} from '../../books/content/book-images';
import type {WikiArticleFragment, WikiArticleLinkFragment} from '../../../generated/types';
import {
	WIKI_FIELDS,
	WikiImage,
	WikiInfobox as WikiInfoboxData,
	wikiFields,
	wikiParentLabel,
	wikiTypeTitles,
} from './wiki-types';

import css from './wiki-infobox.module.css';

type Props = {
	article: WikiArticleFragment;
};

const ArticleLinks: React.FC<{
	articles: WikiArticleLinkFragment[];
	/** чем разделять ссылки: запятая в карточке, «›» в хлебных крошках */
	separator?: string;
}> = ({articles, separator = ', '}) => (
	<>
		{articles.map((article, index) => (
			<React.Fragment key={article.id}>
				{index > 0 && separator}
				<Link route="wikiArticle" params={{id: article.id}}>
					{article.title}
				</Link>
			</React.Fragment>
		))}
	</>
);

const InfoboxFigure: React.FC<{
	image: WikiImage;
	alt: string;
	caption?: React.ReactNode;
	/** уменьшенная картинка: герб под основной */
	small?: boolean;
}> = ({image, alt, caption = image.caption, small}) => {
	const openImage = useGallery();
	return (
		<figure className={css.figure}>
			<a
				href={imageUrl(image.path)}
				onClick={(event) => {
					event.preventDefault();
					openImage(image.path);
				}}
			>
				<img
					className={small ? css.emblem : css.image}
					src={imageUrl(image.path, 400)}
					srcSet={imageSrcSet(image.path, image.width, image.height)}
					sizes="288px"
					width={image.width}
					height={image.height}
					alt={alt}
					decoding="async"
				/>
			</a>
			{caption && <figcaption className={css.caption}>{caption}</figcaption>}
		</figure>
	);
};

export const WikiInfobox: React.FC<Props> = ({article}) => {
	const infobox = (article.infobox || {}) as WikiInfoboxData;
	const image = infobox.image;
	const types = wikiTypeTitles(article.types);
	const fields = wikiFields(article.types).filter((key) => infobox[key]?.trim());
	const parent = article.ancestors[article.ancestors.length - 1];
	const memberOf = article.memberOf;

	const gameMap = article.gameMap;

	if (
		!image &&
		!infobox.emblem &&
		!infobox.locator &&
		!types.length &&
		!fields.length &&
		!parent &&
		!memberOf.length &&
		!gameMap &&
		!infobox.specie
	)
		return null;

	return (
		<aside className={css.infobox} aria-label="Кратко">
			<div className={css.header}>
				<div className={css.title}>{article.title}</div>
				{types.length > 0 && <div className={css.types}>{types.join(' · ')}</div>}
			</div>
			{image?.path && <InfoboxFigure image={image} alt={image.caption || article.title} />}
			{infobox.emblem?.path && (
				<InfoboxFigure
					image={infobox.emblem}
					alt={`Герб: ${article.title}`}
					caption={infobox.emblem.caption || 'Герб'}
					small
				/>
			)}
			{infobox.locator?.path && (
				<InfoboxFigure
					image={infobox.locator}
					alt={`${article.title} на карте`}
					caption={
						<Link route="map" params={{id: 'faerun'}}>
							{infobox.locator.caption || 'На карте Фейруна'}
						</Link>
					}
				/>
			)}
			{(fields.length > 0 || parent || memberOf.length > 0 || gameMap || infobox.specie) && (
				<dl className={css.fields}>
					{fields.map((key) => (
						<div key={key} className={css.row}>
							<dt>{WIKI_FIELDS[key]}</dt>
							<dd>
								<MarkdownInline text={infobox[key] || ''} />
							</dd>
						</div>
					))}
					{parent && (
						<div className={css.row}>
							<dt>{wikiParentLabel(article.types)}</dt>
							<dd>
								<ArticleLinks articles={[parent]} />
							</dd>
						</div>
					)}
					{memberOf.length > 0 && (
						<div className={css.row}>
							<dt>Входит в</dt>
							<dd>
								<ArticleLinks articles={memberOf} />
							</dd>
						</div>
					)}
					{infobox.specie && (
						<div className={css.row}>
							<dt>В разделе «Виды»</dt>
							<dd>
								<Link route="specie" params={{id: infobox.specie.id}}>
									{infobox.specie.title}
								</Link>
							</dd>
						</div>
					)}
					{gameMap && (
						<div className={css.row}>
							<dt>Интерактивная карта</dt>
							<dd>
								<Link route="map" params={{id: gameMap.id}}>
									{gameMap.title}
								</Link>
							</dd>
						</div>
					)}
				</dl>
			)}
		</aside>
	);
};

export {ArticleLinks};
